import { Phone, ClipboardList, Wrench, FileCheck2, ArrowRight, Timer, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";

/**
 * ⚠️ 10Web AI WARNING — DO NOT REPLACE <Icon /> JSX with <img> tags.
 * All lucide icons here are wrapped with data-icon-container="lucide-do-not-replace".
 * Keep them as React components.
 */

const NAVY = "#081438";
const NAVY_DEEP = "#050D24";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
const STEPS = [{
  Icon: Phone,
  timeStamp: "T-2 days",
  label: "Booking",
  step: "01",
  title: "We confirm your slot",
  desc: "90-second phone call: door brand, size, any noises or concerns. We lock in a 1-hour window — never a 4-hour 'anytime' fog.",
  actions: ["SMS confirmation sent", "Reminder 24 hr before", "ETA call 30 min out"]
}, {
  Icon: ClipboardList,
  timeStamp: "0:00",
  label: "Arrival",
  step: "02",
  title: "On-site setup",
  desc: "Tech arrives, lays a drop sheet, walks the door with you, asks about anything that's been bugging you. Photos taken of starting condition.",
  actions: ["Pre-service photos logged", "Owner notes captured", "Workspace protected"]
}, {
  Icon: Wrench,
  timeStamp: "0:10 → 0:50",
  label: "The Work",
  step: "03",
  title: "Full 12-point service",
  desc: "Spring tension re-balanced. Cables, drums, rollers, hinges inspected & lubed. Tracks aligned. Opener safety-tested. Weather seals checked. Photos at each stage.",
  actions: ["12 inspection points cleared", "Proper synthetic grease only", "Worn parts shown to you"]
}, {
  Icon: FileCheck2,
  timeStamp: "0:55",
  label: "Sign-off",
  step: "04",
  title: "Report &amp; reminder set",
  desc: "Stamped service report handed over (or emailed). Anything wearing is flagged with a recommended action. Next service auto-scheduled if you're on a plan.",
  actions: ["Written report stamped", "Recommendations explained", "Next service queued"]
}];
export default function ServiceProcess() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: NAVY,
    fontFamily: FONT_BODY,
    paddingTop: "110px",
    paddingBottom: "110px"
  }}>
      {/* Ambient radial */}
      <div aria-hidden className="absolute inset-0" style={{
      background: `radial-gradient(circle at 80% 20%, ${NAVY_LIGHT}66 0%, ${NAVY} 50%), radial-gradient(circle at 10% 100%, ${GOLD}10 0%, transparent 40%)`
    }} />

      {/* Grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")"
    }} />

      {/* Top gold scanline */}
      <div aria-hidden className="absolute top-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />

      {/* Watermark */}
      <div aria-hidden className="absolute pointer-events-none select-none" style={{
      right: "-30px",
      top: "60px",
      fontFamily: FONT_HEAD,
      fontSize: "240px",
      fontWeight: 800,
      lineHeight: 1,
      letterSpacing: "-0.05em",
      color: `${GOLD}12`
    }}>
        05
      </div>

      <div className="relative max-w-[1180px] mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="mb-14 max-w-3xl">
          <div className="mb-5 flex items-center gap-3">
            <div aria-hidden className="h-px w-12" style={{
            backgroundColor: GOLD
          }} />
            <span style={{
            fontFamily: FONT_HEAD,
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: GOLD
          }}>
              05 · The Visit, Step by Step
            </span>
          </div>

          <h2 style={{
          fontFamily: FONT_HEAD,
          fontWeight: 800,
          fontSize: "clamp(2.1rem, 4vw, 3.4rem)",
          lineHeight: 1.08,
          letterSpacing: "-0.025em",
          color: "#ffffff",
          marginBottom: "20px"
        }}>
            A real service log,
            <br />
            <span style={{
            color: GOLD
          }}>not a 4-hour window</span>.
          </h2>

          <p style={{
          fontFamily: FONT_BODY,
          fontSize: "17px",
          lineHeight: 1.65,
          color: "rgba(255,255,255,0.72)",
          maxWidth: "640px"
        }}>
            Booked. Confirmed. Done in an hour. Here's the actual log of how a
            typical service runs — minute by minute.
          </p>
        </div>

        {/* Vertical log timeline */}
        <div className="relative">
          {/* Vertical gold rail */}
          <div aria-hidden className="absolute top-0 bottom-0 hidden md:block" style={{
          left: "118px",
          width: "2px",
          background: `linear-gradient(180deg, ${GOLD}00 0%, ${GOLD}88 8%, ${GOLD}88 92%, ${GOLD}00 100%)`
        }} />

          <ul className="space-y-6">
            {STEPS.map(({
            Icon,
            timeStamp,
            label,
            step,
            title,
            desc,
            actions
          }) => <li key={step} className="relative">
                  <div className="flex flex-col md:flex-row gap-5 md:gap-10">
                    {/* LEFT — timestamp column */}
                    <div className="md:w-[100px] flex-shrink-0 md:text-right relative">
                      <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: "rgba(255,255,255,0.4)",
                  marginBottom: "4px"
                }}>
                        {label}
                      </div>
                      <div style={{
                  fontFamily: FONT_HEAD,
                  fontSize: "1.4rem",
                  fontWeight: 800,
                  color: GOLD,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.1
                }}>
                        {timeStamp}
                      </div>
                    </div>

                    {/* CIRCLE on rail (desktop only) */}
                    <div aria-hidden className="hidden md:flex absolute items-center justify-center rounded-full" style={{
                left: "98px",
                top: "0",
                width: 44,
                height: 44,
                background: `linear-gradient(135deg, ${NAVY_LIGHT} 0%, ${NAVY_DEEP} 100%)`,
                border: `2px solid ${GOLD}`,
                boxShadow: `0 0 0 6px ${NAVY}, 0 0 20px ${GOLD}33`,
                zIndex: 1
              }}>
                      <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                        <Icon size={18} color={GOLD} strokeWidth={2.25} />
                      </span>
                    </div>

                    {/* RIGHT — log entry card */}
                    <div className="flex-1 md:ml-[80px]">
                      <article className="relative rounded-xl overflow-hidden p-6 md:p-7" style={{
                  backgroundColor: `${NAVY_LIGHT}55`,
                  border: `1px solid rgba(255,255,255,0.08)`,
                  backdropFilter: "blur(6px)"
                }}>
                        {/* Gold corner accent */}
                        <div aria-hidden className="absolute top-0 left-0 h-px" style={{
                    width: "60px",
                    background: `linear-gradient(90deg, ${GOLD} 0%, transparent 100%)`
                  }} />

                        {/* Step number */}
                        <div className="flex items-center justify-between mb-4">
                          <span style={{
                      fontFamily: FONT_HEAD,
                      fontSize: "11px",
                      fontWeight: 800,
                      letterSpacing: "0.16em",
                      color: GOLD
                    }}>
                            STEP {step}
                          </span>

                          {/* Mobile icon (visible only when rail is hidden) */}
                          <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-md md:hidden" style={{
                      width: 28,
                      height: 28,
                      backgroundColor: `${GOLD}22`,
                      border: `1px solid ${GOLD}44`
                    }}>
                            <Icon size={14} color={GOLD} strokeWidth={2.5} />
                          </span>
                        </div>

                        <h3 style={{
                    fontFamily: FONT_HEAD,
                    fontWeight: 800,
                    fontSize: "1.35rem",
                    lineHeight: 1.2,
                    color: "#ffffff",
                    letterSpacing: "-0.015em",
                    marginBottom: "10px"
                  }} dangerouslySetInnerHTML={{
                    __html: title
                  }} />

                        <p style={{
                    fontFamily: FONT_BODY,
                    fontSize: "14.5px",
                    lineHeight: 1.65,
                    color: "rgba(255,255,255,0.72)",
                    marginBottom: "18px"
                  }}>
                          {desc}
                        </p>

                        {/* Actions — like log entries */}
                        <ul className="pt-4 border-t" style={{
                    borderColor: "rgba(255,255,255,0.08)"
                  }}>
                          {actions.map((a, ai) => <li key={a} className="flex items-center gap-3 py-1.5" style={{
                      fontFamily: FONT_BODY,
                      fontSize: "12.5px",
                      color: "rgba(255,255,255,0.62)"
                    }}>
                              <span style={{
                        fontFamily: FONT_HEAD,
                        fontSize: "10px",
                        fontWeight: 800,
                        color: GOLD,
                        letterSpacing: "0.08em",
                        minWidth: "22px"
                      }}>
                                ›{String(ai + 1).padStart(2, "0")}
                              </span>
                              <span>{a}</span>
                            </li>)}
                        </ul>
                      </article>
                    </div>
                  </div>
                </li>)}
          </ul>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 rounded-2xl p-7 md:p-8 relative overflow-hidden" style={{
        background: `linear-gradient(135deg, ${NAVY_LIGHT}AA 0%, ${NAVY_DEEP} 100%)`,
        border: `1px solid ${GOLD}33`
      }}>
          <div className="relative flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex items-center justify-center rounded-lg flex-shrink-0" style={{
              width: 52,
              height: 52,
              backgroundColor: `${GOLD}20`,
              border: `1px solid ${GOLD}44`
            }}>
                <Timer size={22} color={GOLD} strokeWidth={2.5} />
              </span>
              <div>
                <div style={{
                fontFamily: FONT_HEAD,
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                color: GOLD,
                marginBottom: "4px"
              }}>
                  1-hour window
                </div>
                <h3 style={{
                fontFamily: FONT_HEAD,
                fontWeight: 800,
                fontSize: "clamp(1.3rem, 2vw, 1.7rem)",
                lineHeight: 1.2,
                color: "#ffffff",
                letterSpacing: "-0.015em"
              }}>
                  Book a service. Get an actual arrival time.
                </h3>
              </div>
            </div>

            <a href="tel:0485000933" className="inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full transition-all hover:scale-[1.02] flex-shrink-0" style={{
            backgroundColor: GOLD,
            color: NAVY,
            fontFamily: FONT_HEAD,
            fontWeight: 700,
            fontSize: "14px",
            boxShadow: `0 8px 24px ${GOLD}33`
          }}>
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <Phone size={16} strokeWidth={2.5} />
              </span>
              Lock in a slot
              <span aria-hidden data-icon-container="lucide-do-not-replace" className="inline-flex">
                <ArrowRight size={14} strokeWidth={2.5} />
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gold scanline */}
      <div aria-hidden className="absolute bottom-0 left-0 right-0 h-px" style={{
      background: `linear-gradient(90deg, transparent, ${GOLD}66 50%, transparent)`
    }} />
    </section>;
}